import { useContext, useEffect, useState } from 'react'
import ReactDatePicker from "react-datepicker";
import Select from "react-select";
import { getBeatsApi, getReceiptReportApi, getUserListApi } from '../api/api.js'
import { showMessage } from '../context/MessageContext.jsx';
import { AuthContext } from '../context/AuthContext.jsx'
import { useOrganizationSettings } from '../context/OrganizationSettingsContext';
import { useCompany } from '../context/CompanyContext.jsx';
import Pagination from '../components/ui/Pagination.jsx'
import { usePagination } from '../hooks/usePagination.js'

export default function VouchersList() {
  const [limit] = useState(30); // Items per page
  const [total, setTotal] = useState(0);
  const {user } = useContext(AuthContext);
  const { settings } = useOrganizationSettings();
  const { companyId } = useCompany();
  const pagination = usePagination(total, limit);

  const [beats, setBeats] = useState([]);
  const [userList, setUserList] = useState([]);
  const [selectedBeat, setSelectedBeat] = useState('');
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedUserLabel, setSelectedUserLabel] = useState(null);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());

  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [downloading, setDownloading] = useState(false)

  // Fetch all beats on mount
  useEffect(() => {
    getBeatsApi()
      .then(res => setBeats(res.data?.data || res.data || []))
      .catch(() => setBeats([]));
  }, []);
  
  // Fetch all users on mount (only if userWise is enabled and user is owner)
  useEffect(() => {
    if (settings.userWise && user?.isOwner) {
      getUserListApi()
        .then(res => {
          const users = res.data?.data || res.data || [];
          setUserList(users);
          
          // Set default selection to logged-in user for owners
          if (user?.tallyUserId && users.length > 0) {
            const defaultUser = users.find(sp => {
              const spId = sp.tallyUserId || sp.tallyUserId;
              const userId = user.tallyUserId || user.tallyUserId;
              return spId === userId;
            });
            if (defaultUser) {
              setSelectedUserId(defaultUser.tallyUserId);
              setSelectedUserLabel(`${defaultUser.userName} - ${defaultUser.tallyUserId}`);
            }
          }
        })
        .catch(() => setUserList([]));
    }
  }, [settings.userWise, user?.isOwner, user?.tallyUserId]);



  useEffect(() => {
    setLoading(true);
    const params = {
      ...(settings.beatWise && selectedBeat ? { beatName: selectedBeat } : {}),
      // For userWise: owners can select, non-owners auto-filter by logged-in user
      ...(settings.userWise ? {
        tallyUserId: user?.isOwner ? (selectedUserId || user?.tallyUserId) : user?.tallyUserId
      } : {}),
      // Company filtering (Case 3 & 4) - use global company from context
      ...(settings.userWise && companyId ? { companyId: companyId } : {}),
      ...(startDate && endDate ? {
      startDate: startDate.toISOString().slice(0, 10),
      endDate: endDate.toISOString().slice(0, 10)
    } : {}),
      page: pagination.currentPage,
      limit,
    };
    getReceiptReportApi(params)
    .then((res) => {
      const data = res.data?.data || res.data || [];
      setRows(data.rows || data); // Adjust if your API returns { rows, total }
      setTotal(res.data.totalCount || 0);
    })
    .catch(() => {
      setRows([]);
      setTotal(0);
    })
    .finally(() => setLoading(false));
  }, [selectedBeat, selectedUserId, companyId, startDate, endDate, pagination.currentPage, limit, settings.beatWise, settings.userWise, user?.isOwner, user?.tallyUserId]);

  const handleBeatChange = e => {
    if (e === null) {
      setSelectedBeat(null);
    } else {
      setSelectedBeat(e.value);
    }
    pagination.resetPagination();
  };

  const handleUserChange = (selectedOption) => {
    // Only update if a selection was actually made (not when clicking outside)
    if (selectedOption === null) {
      // Only clear if explicitly cleared (X button), not on outside click
      setSelectedUserId(null);
      setSelectedUserLabel(null);
    } else if (selectedOption && selectedOption.value) {
      setSelectedUserId(selectedOption.value);
      setSelectedUserLabel(selectedOption.label);
    }
    pagination.resetPagination();    
  };

  const handleStartDateChange = (date) => {
    // Check if a date is selected
    if (date) {
      // Get the UTC time by stripping the local timezone offset
      const utcDate = new Date(
        date.getTime() - date.getTimezoneOffset() * 60000
      );
      setStartDate(utcDate);

    } else {
      setStartDate(new Date());
    }
  }
  
  const handleEndDateChange = (date) => {
    // Check if a date is selected
    if (date) {
      // Get the UTC time by stripping the local timezone offset
      const utcDate = new Date(
        date.getTime() - date.getTimezoneOffset() * 60000
      );
      setEndDate(utcDate);

    } else {
      setEndDate(new Date());
    }
  }


  return (
    <div className="card">
      <h2>User-wise Collections</h2>
      <div className="filter-container">
        <div className="d-flex flex-column flex-md-row gap-2" style={{ flex: 1 }}>
          
          {/* User */}
          {settings.userWise && user?.isOwner && (
            <div className="filter-item">
              <label className="form-label small fw-500 mb-2 d-block">User</label>
              <Select
                options={userList.map(b => ({ value: b.tallyUserId, label: `${b.userName} - ${b.tallyUserId}` }))}
                value={
                  selectedUserId && selectedUserLabel
                    ? userList.find(u => u.tallyUserId === selectedUserId)
                      ? { value: selectedUserId, label: selectedUserLabel }
                      : null
                    : null
                }
                onChange={handleUserChange}
                onBlur={() => {}} // Prevent clearing on blur
                placeholder="Select User"
                isClearable={true}
                isSearchable
                className="react-select-container"
                classNamePrefix="react-select"
              />
            </div>
          )}

          {/* Beat */}
          {settings.beatWise && (
            <div className="filter-item">
              <label className="form-label small fw-500 mb-2 d-block">Beat</label>
              <Select
                options={beats.map(b => ({ value: b.beatName, label: b.beatName }))}
                value={selectedBeat ? { value: selectedBeat, label: selectedBeat } : null}
                onChange={handleBeatChange}
                placeholder="Select Beat"
                isClearable={true}
                isSearchable
                className="react-select-container"
                classNamePrefix="react-select"
              />
            </div>
          )}

          {/* Start Date */}
          <div className="filter-item">
            <label className="form-label small fw-500 mb-2 d-block">Start Date</label>
            <ReactDatePicker
              selected={startDate}
              onChange={handleStartDateChange}
              className="form-control form-control-sm"
              dateFormat="dd-MM-yyyy"
              maxDate={new Date()}
              placeholderText="Start Date"
            />
          </div>

          {/* End Date */}
          <div className="filter-item">
            <label className="form-label small fw-500 mb-2 d-block">End Date</label>
            <ReactDatePicker
              selected={endDate}
              onChange={handleEndDateChange}
              className="form-control form-control-sm"
              dateFormat="dd-MM-yyyy"
              maxDate={new Date()}
              placeholderText="End Date"
            />
          </div>
        </div>
      </div>
      {loading ? 'Loading...' : (
        <div className="table-wrap">
          <table className="table table-striped">
            <thead>
              <tr>
                <th>S.No</th>
                <th>User</th>
                <th>User ID</th>
                <th>Cash</th>
                <th>UPI</th>
                <th>Cheque</th>
                <th>Advance</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan="8" style={{ textAlign: 'center', padding: '2rem', color: '#546e7a' }}>
                    No records available
                  </td>
                </tr>
              ) : (
                rows.map((r, idx) => (
                  <tr key={r.id || idx}>
                    <td>{idx + 1}</td>
                    <td>{r.userName}</td>
                    <td>{r.tallyUserId}</td>
                    <td>{r.totalCash}</td>
                    <td>{r.totalUpi}</td>
                    <td>{r.totalCheque}</td>
                    <td>{r.totalAdvance}</td>
                    <td>{r.totalAmount}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          {!loading && total > 0 && (
            <Pagination
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              onPageChange={pagination.goToPage}
            />
          )}
        </div>
      )}
    </div>
  )
}
